import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getProfessionalResume, updateProfessionalResume } from '../../../../../services/pp';
import { showSuccessMessage } from '../../../../../utils';

const initialExperience = {
  employerName: '',
  title: '',
  isCurrentEmployer: false,
  startDate: '',
  endDate: '',
};

const initialEducation = {
  institutionName: '',
  currentlyEnrolled: false,
  startDate: '',
  endDate: '',
};

export const useProfessionalResume = (professionalId) => {
  const [formValues, setFormValues] = useState({});

  const invalidForm = useMemo(() => {
    const isSummaryIncomplete = !formValues.summary;

    const cleanedExperiences =
      formValues.experiences?.filter(
        (exp) => exp?.employerName || exp?.title || exp?.startDate || exp?.endDate,
      ) || [];
    const isExperienceIncomplete =
      cleanedExperiences?.length === 0 ||
      cleanedExperiences?.some(
        (exp) =>
          !exp.employerName ||
          !exp.title ||
          !exp.startDate ||
          (!exp.isCurrentEmployer && !exp.endDate),
      );

    const cleanedEducations =
      formValues.educations?.filter(
        (edu) => edu?.institutionName || edu?.startDate || edu?.endDate,
      ) || [];
    const isEducationIncomplete =
      cleanedEducations?.length === 0 ||
      cleanedEducations?.some(
        (edu) => !edu?.institutionName || (!edu?.currentlyEnrolled && !edu?.endDate),
      );

    return isSummaryIncomplete || isExperienceIncomplete || isEducationIncomplete;
  }, [formValues]);

  const {
    data: response,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['professionalResume', professionalId],
    queryFn: () => getProfessionalResume(professionalId),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const updateResume = useMutation({
    mutationFn: (payload) => updateProfessionalResume(professionalId, payload),
    onSuccess: async () => {
      await refetch();
      showSuccessMessage({ message: 'Resume updated successfully' });
    },
  });

  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleExperienceChange = (index, field, value) => {
    setFormValues((prev) => {
      const experiences = [...(prev.experiences || [])];

      if (!experiences[index]) {
        experiences[index] = { ...initialExperience };
      }

      experiences[index][field] = value;

      if (field === 'isCurrentEmployer' && !!value) {
        experiences[index].endDate = '';
      }

      return { ...prev, experiences };
    });
  };

  const handleEducationChange = (index, field, value) => {
    setFormValues((prev) => {
      const educations = [...(prev.educations || [])];

      if (!educations[index]) {
        educations[index] = { ...initialEducation };
      }

      educations[index][field] = value;

      if (field === 'currentlyEnrolled' && !!value) {
        educations[index].endDate = '';
      }

      return { ...prev, educations };
    });
  };

  const handleSaveResume = () => {
    // remove empty experiences
    const cleanedExperiences = (
      formValues.experiences?.filter(
        (exp) => exp?.employerName || exp?.title || exp?.startDate || exp?.endDate,
      ) || []
    ).map((exp) => ({
      ...exp,
      startDate: exp.startDate ? new Date(exp.startDate) : null,
      endDate: exp.endDate ? new Date(exp.endDate) : null,
    }));

    // remove empty educations
    const cleanedEducations = (
      formValues.educations?.filter(
        (edu) => edu?.institutionName || edu?.startDate || edu?.endDate,
      ) || []
    ).map((edu) => ({
      ...edu,
      startDate: edu.startDate ? new Date(edu.startDate) : null,
      endDate: edu.endDate ? new Date(edu.endDate) : null,
    }));

    const payload = {
      summary: formValues.summary || '',
      experiences: cleanedExperiences,
      educations: cleanedEducations,
    };

    updateResume.mutate(payload);
  };

  const populateFormValues = useCallback(() => {
    if (response?.data) {
      setFormValues({
        summary: response.data.summary,
        experiences: (response.data.experiences || []).map((exp) => ({ ...exp })),
        educations: (response.data.education || []).map((edu) => ({ ...edu })),
      });
    }
  }, [response]);

  useEffect(() => {
    populateFormValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data: response?.data,
    isLoading,
    isError,
    error,
    formValues,
    invalidForm,
    resetForm: populateFormValues,
    handleChange,
    handleExperienceChange,
    handleEducationChange,
    handleSaveResume,
    updateResume,
  };
};
